/**
 * `<turn-log>`: the activity feed - melds, books, Feet picked up and players
 * going out - growing up from the bottom like a chat, newest last, with a
 * divider opening each round.
 *
 * The feed is deliberately sparse, but the live regions are not: every action,
 * draws and discards included, is still announced politely, so a player who
 * never sees a card move learns everything a sighted player does. Assertive
 * is reserved for "it is your turn", and carries the next step.
 */

import { h } from "../dom.js";
import type { LoggedEvent } from "../log.js";
import { BaseElement } from "./base-element.js";
import styles from "./turn-log.css";

export class TurnLog extends BaseElement {
    #events: readonly LoggedEvent[] = [];

    #announcement = "";

    #alert: string | null = null;

    #roundLive = false;

    readonly #entries = h("ol", { class: "entries" });

    readonly #polite = h("div", { "class": "sr-only", "aria-live": "polite", "aria-atomic": "true" });

    readonly #assertive = h("div", { "class": "sr-only", "aria-live": "assertive", "aria-atomic": "true" });

    constructor() {
        super(styles);

        this.root.append(
            h("h2", { class: "heading label" }, "Activity"),
            this.#entries,
            this.#polite,
            this.#assertive
        );
    }

    set events(value: readonly LoggedEvent[]) {
        this.#events = value;
        this.invalidate();
    }

    /** The latest action in full, for screen readers, whether or not the feed shows it. */
    set announcement(value: string) {
        this.#announcement = value;
        this.invalidate();
    }

    set alert(value: string | null) {
        this.#alert = value;
        this.invalidate();
    }

    /**
     * Whether a round is in play. While it is, that round's divider is pinned
     * to the top of the feed once enough activity scrolls it up there, so the
     * round and its opener stay in sight; it is let go when the round is scored.
     */
    set roundLive(value: boolean) {
        this.#roundLive = value;
        this.invalidate();
    }

    protected render(): void {
        this.setAttribute("role", "region");
        this.setAttribute("aria-label", "Activity");

        const currentRoundId = this.#roundLive
            ? this.#events.findLast((event) => event.kind === "round")?.id
            : undefined;

        // Newest first in the document - the column is reversed on screen, which
        // pins the newest entry to the bottom and keeps the scroll anchored there.
        this.#entries.replaceChildren(...this.#events.toReversed().map((event) => event.kind === "round"
            ? h("li", { class: event.id === currentRoundId ? "round pinned" : "round" }, h("span", {}, event.text))
            : h("li", { class: event.automated ? "entry automated" : "entry" }, event.text)));

        // Only write when the text changes; rewriting the same words re-announces them.
        if (this.#polite.textContent !== this.#announcement) {
            this.#polite.textContent = this.#announcement;
        }

        const alert = this.#alert ?? "";

        if (this.#assertive.textContent !== alert) {
            this.#assertive.textContent = alert;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "turn-log": TurnLog;
    }
}

customElements.define("turn-log", TurnLog);
